import React, { useMemo } from 'react';
import { Select, MenuItem, Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface TimeSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  intervalMinutes?: number;
}

const generateHourOptions = (): string[] =>
  Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

const generateMinuteOptions = (interval: number): string[] =>
  Array.from({ length: Math.floor(60 / interval) }, (_, i) =>
    String(i * interval).padStart(2, '0')
  );

const TimeSelect: React.FC<TimeSelectProps> = ({
  label,
  value,
  onChange,
  fullWidth = false,
  sx,
  intervalMinutes = 15,
}) => {
  const hours = useMemo(() => generateHourOptions(), []);
  const minutes = useMemo(() => generateMinuteOptions(intervalMinutes), [intervalMinutes]);

  const [currentHour, currentMinute] = value ? value.split(':') : ['00', '00'];

  const handleHourChange = (h: string) => {
    onChange(`${h}:${currentMinute}`);
  };

  const handleMinuteChange = (m: string) => {
    onChange(`${currentHour}:${m}`);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.23)',
        borderRadius: 2,
        bgcolor: '#fff',
        overflow: 'hidden',
        width: fullWidth ? '100%' : 'auto',
        '&:hover': {
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
        '&:focus-within': {
          borderColor: 'primary.main',
          borderWidth: 2,
          m: '-1px',
        },
        ...(sx as any),
      }}
    >
      <Select
        value={currentHour}
        onChange={(e) => handleHourChange(e.target.value)}
        variant="standard"
        disableUnderline
        MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
        sx={{
          flex: 1,
          '& .MuiSelect-select': {
            py: '16px',
            pl: '14px',
            pr: '8px !important',
            textAlign: 'center',
            color: 'text.tertiary',
          },
          '& .MuiSelect-icon': { display: 'none' },
        }}
      >
        {hours.map((h) => (
          <MenuItem key={h} value={h}>{h}</MenuItem>
        ))}
      </Select>

      <Box sx={{ color: 'text.tertiary', userSelect: 'none', fontSize: '1rem', lineHeight: 1 }}>:</Box>

      <Select
        value={currentMinute}
        onChange={(e) => handleMinuteChange(e.target.value)}
        variant="standard"
        disableUnderline
        MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
        sx={{
          flex: 1,
          '& .MuiSelect-select': {
            py: '16px',
            pl: '8px',
            pr: '32px !important',
            textAlign: 'center',
            color: 'text.tertiary',
          },
        }}
      >
        {minutes.map((m) => (
          <MenuItem key={m} value={m}>{m}</MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default TimeSelect;
